import { useRef } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy.js';

import { pageTypeConstants as PAGE_TYPES } from '../constants/pageTypeConstants.ts';
import { QP_PAGE_SIZE_VALUE_DEFAULT } from '../constants/queryParamConstants.ts';

import { BuySharedBreadcrumbs } from './BuySharedBreadcrumbs/BuySharedBreadcrumbs.jsx';
import { BuySharedHead } from './BuySharedHead/BuySharedHead.tsx';
import { BuySharedTitle } from './BuySharedTitle/BuySharedTitle.jsx';
import { BuySharedRelatedPages } from './BuySharedRelatedPages/BuySharedRelatedPages.tsx';
import { BuySharedResultContainer } from './BuySharedResultContainer/BuySharedResultContainer.tsx';
import { BuySharedAsyncModules } from './BuySharedAsyncModules/BuySharedAsyncModules.tsx';
import { SharedPagination } from '../shared/SharedPagination/SharedPagination.tsx';
import { BuySharedDescription } from './BuySharedDescription/BuySharedDescription.jsx';
import { BuySharedNoInventoryAvailable } from './BuySharedNoInventoryAvailable/BuySharedNoInventoryAvailable.tsx';
import { SharedRespLinksBlock } from '../shared/SharedRespLinksBlock/SharedRespLinksBlock.tsx';
import { SharedBottomModule } from '../shared/SharedBottomModule/SharedBottomModule.tsx';
import { SharedWordpressArticles } from '../shared/SharedWordpressArticles/SharedWordpressArticles.jsx';
import { SharedFollowTopButton } from '../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';
import { SharedFollowSearchBanner } from '../shared/SharedFollowSearchBanner/SharedFollowSearchBanner.tsx';
import { BuyRespRefineMenu } from './BuyRespRefineMenu/BuyRespRefineMenu.tsx';
import { BuyRespTopNav } from './BuyRespTopNav/BuyRespTopNav.tsx';
import { BuySharedRefetchContainer } from './BuySharedRefetchContainer.jsx';
import { SharedSaveSearchContextProvider } from '../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { useServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSbSelector } from '../reducers/useSbSelector.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { SbSharedFavoritesNudgeToast } from '../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast.tsx';

import styles from './BuySharedLayout.scss';

const BuyRespLayoutComponent = ({ viewer, relay }) => {
    const {
        buyPage,
        user = null,
        isWordpressArticlesEnabled,
        isNlgContentEnabled,
        isRelatedBuyPagesEnabled,
    } = viewer;
    const {
        internalBuyLinks,
        bottomModule,
        filters,
        displayMaxNumberOfPages,
        totalResults,
        appliedFilters,
        displayUriRef,
    } = buyPage;
    const userId = user?.serviceId;

    const { isItemFavoritingFeb2026Variant, isItemFavoritingFeb2026Control } =
        useServerVarsContext();
    const page = useSbSelector(state => state.filters.page);

    const searchTerm = appliedFilters?.find(f => f?.name === PAGE_TYPES.BUY)?.values?.[0]
        ?.displayName;

    // Save initial value in case totalResults changes on refetch
    const shouldShowRefineMenu = useRef(Number(totalResults) > QP_PAGE_SIZE_VALUE_DEFAULT);

    useDwelledOnPage(PAGE_TYPE.SEARCH);

    return (
        <SharedSaveSearchContextProvider
            itemSearch={buyPage}
            user={user}
            searchTerm={searchTerm}
            uriRef={displayUriRef}
        >
            <>
                <BuySharedHead buyPage={buyPage} />
                <BuySharedRefetchContainer
                    viewer={viewer}
                    buyPage={buyPage}
                    refetch={relay.refetch}
                />
                <BuySharedBreadcrumbs buyPage={buyPage} />
                <div className={styles.headerLeftWrapper}>
                    <div className={styles.headerLeft}>
                        <BuySharedTitle buyPage={buyPage} />
                        {!shouldShowRefineMenu.current && (
                            <SharedFollowTopButton
                                itemSearch={buyPage}
                                user={user}
                                addLeadingSpace
                            />
                        )}
                    </div>
                </div>

                {isRelatedBuyPagesEnabled && <BuySharedRelatedPages buyPage={buyPage} />}

                <div className={styles.spacer} />

                <div className={styles.mainContent}>
                    {shouldShowRefineMenu.current && (
                        <BuyRespRefineMenu
                            viewer={viewer}
                            itemSearch={buyPage}
                            user={user}
                            filters={filters}
                        />
                    )}
                    <div className={styles.container}>
                        {totalResults ? (
                            <>
                                {shouldShowRefineMenu.current && (
                                    <BuyRespTopNav buyPage={buyPage} user={user} />
                                )}
                                <BuySharedResultContainer
                                    viewer={viewer}
                                    buyPage={buyPage}
                                    user={user}
                                    shouldShowRefineMenu={shouldShowRefineMenu.current}
                                />
                                <SharedPagination
                                    currentPage={page}
                                    numberOfPages={displayMaxNumberOfPages}
                                    displayUriRef={displayUriRef}
                                    hideHref
                                />
                            </>
                        ) : (
                            <BuySharedNoInventoryAvailable />
                        )}
                    </div>
                </div>

                <BuySharedAsyncModules viewer={viewer} buyPage={buyPage} user={user} />

                {/* Follow Search */}
                <SharedFollowSearchBanner itemSearch={buyPage} user={user} />

                {isNlgContentEnabled && (
                    <SharedBottomModule
                        className={styles.bottomModule}
                        bottomModule={bottomModule}
                    />
                )}

                <BuySharedDescription buyPage={buyPage} />

                {isWordpressArticlesEnabled && (
                    <SharedWordpressArticles
                        itemSearch={buyPage}
                        headerClassName={styles.wpArticlesHeader}
                        launchOrigin={PAGE_TYPE.BUY}
                    />
                )}

                <SharedRespLinksBlock
                    item={null}
                    viewer={viewer}
                    itemSearch={buyPage}
                    links={internalBuyLinks}
                    popularSearchTermClassName={styles.popularSearchCategoryTerm}
                />

                <SbSharedFavoritesNudgeToast
                    userId={userId}
                    isItemFavoritingFeb2026Variant={isItemFavoritingFeb2026Variant}
                    isItemFavoritingFeb2026Control={isItemFavoritingFeb2026Control}
                />
            </>
        </SharedSaveSearchContextProvider>
    );
};

BuyRespLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
};

export const BuyRespLayout = createRefetchContainer(
    BuyRespLayoutComponent,
    {
        viewer: graphql`
            fragment BuyRespLayout_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                isNlgContentEnabled: isEnabledByLocale(featureName: "NLG_CONTENT")
                isRelatedBuyPagesEnabled: isEnabledByLocale(featureName: "RELATED_BUY_PAGES")
                buyPage: itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                    regions: $regions
                    numOfItemsToInflate: $numOfItemsToInflate
                ) {
                    totalResults
                    displayMaxNumberOfPages
                    displayUriRef
                    appliedFilters {
                        name
                        values {
                            displayName
                        }
                    }
                    filters {
                        ...BuyRespRefineMenu_filters
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    ...BuySharedRefetchContainer_buyPage
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedAsyncModules_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...SharedWordpressArticles_itemSearch
                    ...SharedRespLinksBlock_itemSearch
                    ...SbSharedBillboardAd_itemSearch
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                    ...BuyRespRefineMenu_itemSearch
                    ...SharedSaveSearchContext_itemSearch
                    ...SharedFollowTopButton_itemSearch
                    ...SharedFollowSearchBanner_itemSearch
                    ...BuyRespTopNav_buyPage
                }
                ...BuySharedAsyncModules_viewer
                ...SharedRespLinksBlock_viewer
                ...BuySharedResultContainer_viewer
                ...BuyRespRefineMenu_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...BuyRespRefineMenu_user
                    ...BuySharedResultContainer_user
                    ...BuySharedAsyncModules_user
                    ...SharedSaveSearchContext_user
                    ...SharedFollowTopButton_user
                    ...SharedFollowSearchBanner_user
                    ...BuyRespTopNav_user
                }
            }
        `,
    },
    graphql`
        query BuyRespLayoutRefetchQuery(
            $uriRef: String!
            $localeOrigin: String!
            $isClient: Boolean!
            $afterDisplayOrBtSsr: Boolean!
            $isTrade: Boolean!
            $showSeller: Boolean!
            $first: Int!
            $page: Int!
            $rootId: String!
            $fetchBuy: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $userId: String = ""
            $personalizationId: String = ""
            $guestId: String = ""
            $fetchUser: Boolean!
            $skipUser: Boolean = true
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $followSearchPages: [String]
            $priceBookName: String
            $regions: String = ""
            $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
            $fetchTileVideo: Boolean = false
            $numOfItemsToInflate: Int
            $topItemIds: [String]
            $categoryLevel: Int
        ) {
            viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                buyPage: itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                    regions: $regions
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @include(if: $fetchBuy) {
                    totalResults
                    displayMaxNumberOfPages
                    displayUriRef
                    appliedFilters {
                        name
                        values {
                            displayName
                        }
                    }
                    filters {
                        ...BuyRespRefineMenu_filters
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    ...BuySharedRefetchContainer_buyPage
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedAsyncModules_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...SharedWordpressArticles_itemSearch
                    ...SharedRespLinksBlock_itemSearch
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                    ...BuyRespRefineMenu_itemSearch
                    ...SharedSaveSearchContext_itemSearch
                    ...SharedFollowTopButton_itemSearch
                    ...BuyRespTopNav_buyPage
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...BuyRespRefineMenu_user
                    ...BuySharedResultContainer_user
                    ...BuySharedAsyncModules_user
                    ...SharedSaveSearchContext_user
                    ...SharedFollowTopButton_user
                    ...SharedFollowSearchBanner_user
                    ...BuyRespTopNav_user
                }
                ...BuySharedAsyncModules_viewer
                ...BuyRespRefineMenu_viewer
                ...BuySharedResultContainer_viewer
            }
        }
    `
);
